import React, { createContext, useContext, useState, useEffect, startTransition } from 'react';

const GlobalContext = createContext();

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalContextProvider');
  }
  return context;
};

export const GlobalContextProvider = ({ children }) => {
  /* How the work is shown: 'images' (the tiles), 'numbers' (the plain list),
     or 'table' (the index, carried over from the old site). One value rather
     than a flag each, because they are alternatives — two booleans could
     disagree, and did.

     Older visitors have the two booleans in their browser from before, so read
     those once and translate. */
  const [view, setView] = useState(() => {
    const stored = localStorage.getItem('view');
    if (stored === 'images' || stored === 'numbers' || stored === 'table') return stored;
    try {
      const legacy = localStorage.getItem('numbersActive');
      if (legacy !== null && JSON.parse(legacy)) return 'numbers';
    } catch (e) { /* nothing worth doing about a bad stored value */ }
    return 'images';
  });

  const imagesActive = view === 'images';
  const numbersActive = view === 'numbers';
  const tableActive = view === 'table';

  const [tokenSearchVal, setTokenSearchVal] = useState('');

  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    return stored !== null ? JSON.parse(stored) : false; // default to false (light mode)
  });

  const show = (next) => () => startTransition(() => setView(next));
  const handleNumberClick = show('numbers');
  const handleImageClick = show('images');
  const handleTableClick = show('table');

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      return newValue;
    });
  };

  useEffect(() => {
    localStorage.setItem('view', view);
  }, [view]);

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  const value = {
    view,
    imagesActive,
    numbersActive,
    tableActive,
    handleNumberClick,
    handleImageClick,
    handleTableClick,
    tokenSearchVal,
    setTokenSearchVal,
    darkMode,
    toggleDarkMode,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
};

